/**
 * Formularzustand und Request-Rümpfe für „Zähler anlegen" / „Zähler
 * bearbeiten" (Spec 46a, „Blatt"). Reine Abbildung der Felder auf die
 * API-Formen; geprüft wird am Server (Stellenzahl, Frist, Dublette …), der
 * Client sendet den Text, den die Person eingegeben hat.
 *
 * Die Vorgaben (6 Stellen, Eintarif, 90 Tage) sind die der API
 * (`MeterCreate`); sie stehen hier nur, damit das Formular nicht leer
 * aussieht — wer sie nicht ändert, bekommt dasselbe wie ohne Angabe.
 */

import type { components } from '@/lib/api-types'
import type { MeterCreate, MeterDetail, MeterUpdate } from '@/features/energy/api'

type MeterKindValue = components['schemas']['MeterKind']
type MeterTariffValue = components['schemas']['MeterTariff']

export interface MeterFormValues {
  readonly kind: string
  readonly serial: string
  readonly purpose: string
  readonly digits: string
  readonly tariff: string
  readonly operator: string
  readonly interval: string
}

export const EMPTY_METER_FORM: MeterFormValues = {
  kind: '',
  serial: '',
  purpose: '',
  digits: '6',
  tariff: 'single',
  operator: '',
  interval: '90',
}

/** Die auswählbaren Stellenzahlen (Fachregel 6: 4 bis 9). */
export const DIGIT_OPTIONS = ['4', '5', '6', '7', '8', '9'].map((value) => ({ value, label: value }))

export function deriveMeterForm(meter: MeterDetail): MeterFormValues {
  return {
    kind: meter.kind.value,
    serial: meter.serial,
    purpose: meter.purpose ?? '',
    digits: String(meter.digits),
    tariff: meter.tariff.value,
    operator: meter.operator ?? '',
    interval: String(meter.reading_interval_days),
  }
}

/** Das Feld „Ablesefrist" nimmt nur Ziffern an. */
export function digitsOnly(value: string): string {
  return value.replace(/\D/g, '')
}

/** Haus, Art und nicht-leere Nummer (Spec 46a, `canSave`). */
export function canSaveMeter(houseId: string | undefined, form: MeterFormValues): boolean {
  return houseId !== undefined && form.kind !== '' && form.serial.trim() !== ''
}

/** Leeres Feld → `null` (auch von `supply-form.ts` genutzt). */
export function trimmedOrNull(value: string): string | null {
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}

export function toMeterCreate(houseId: string, form: MeterFormValues): MeterCreate {
  return {
    house_id: houseId,
    kind: form.kind as MeterKindValue,
    serial: form.serial,
    purpose: trimmedOrNull(form.purpose),
    digits: Number(form.digits),
    tariff: form.tariff as MeterTariffValue,
    operator: trimmedOrNull(form.operator),
    // Leer ergibt 0 — der Server lehnt das am Feld ab („Zwischen 1 und 3650 Tagen").
    reading_interval_days: Number(form.interval),
  }
}

/** Geleerte optionale Felder gehen über `clear`, nicht als `null` im Rumpf
 * (Spec 46a, Fachregel 4/8) — und nur, wenn vorher etwas drinstand. */
export function toMeterUpdate(meter: MeterDetail, form: MeterFormValues): MeterUpdate {
  const purpose = trimmedOrNull(form.purpose)
  const operator = trimmedOrNull(form.operator)
  const clear = [
    ...(purpose === null && meter.purpose !== null ? ['purpose'] : []),
    ...(operator === null && meter.operator !== null ? ['operator'] : []),
  ]

  return {
    serial: form.serial,
    digits: Number(form.digits),
    tariff: form.tariff as MeterTariffValue,
    ...(purpose === null ? {} : { purpose }),
    ...(operator === null ? {} : { operator }),
    reading_interval_days: Number(form.interval),
    ...(clear.length === 0 ? {} : { clear }),
  }
}
